import { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, Search, ChevronDown, Bell, Settings, X } from 'lucide-react';
import { endCookieSession } from '@/v2/utils/auth-flow';
import { useAuthStore } from '@/stores/auth';
import {
  Avatar,
  toast,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/v2/design-system/primitives';
import { TenantSwitcher } from './TenantSwitcher';
import { NAV_ALL } from './nav';
import { GlobalPatientSearch } from './GlobalPatientSearch';
import { NotificationDrawer } from './NotificationDrawer';
import { startRoleTour } from '@/v2/components/tour/GuidedTour';
import { useConfirmDiscardWork } from './useConfirmDiscardWork';
import { useCanAccess } from '@/hooks/useCanAccess';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  secretary: 'Secretária',
  professional: 'Profissional',
};

function usePageTitle(): string {
  const { pathname } = useLocation();
  if (pathname.replace(/\/+$/, '').toLowerCase() === '/clinicas') return 'Clínicas';
  const match = NAV_ALL.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  return match?.label ?? 'ValiosasCRM';
}

function UserMenu() {
  const discardWork = useConfirmDiscardWork();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canViewBilling = useCanAccess('billing:view');

  const handleLogout = async () => {
    try {
      if (await endCookieSession(queryClient)) navigate('/login', { replace: true });
    } catch {
      toast.error('Não foi possível encerrar o acesso assistido. Verifique a conexão e tente novamente.');
    }
  };

  const name = user?.name ?? 'Operador';
  const role = user ? (ROLE_LABEL[user.role] ?? user.role) : 'Demo';
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
    {discardWork.confirmation}
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Menu de ${name}`} className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2">
          <Avatar fallback={initials} gradient="g5" size="md" src={user?.avatarUrl} alt={name} />
          <div className="hidden text-left sm:block">
            <div className="text-so-14 font-bold leading-tight text-sober-text">{name}</div>
            <div className="text-so-12 text-sober-secondary">{role}</div>
          </div>
          <ChevronDown className="hidden size-4 text-sober-secondary sm:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem asChild>
          <Link to="/config/perfil">Meu perfil</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/config">Configurações</Link>
        </DropdownMenuItem>
        {/* Classe A: /billing exige billing:view — sem gate aqui, o profissional
            clicava e era devolvido ao dashboard sem uma palavra de explicação. */}
        {canViewBilling && (
          <DropdownMenuItem asChild>
            <Link to="/billing">Assinatura</Link>
          </DropdownMenuItem>
        )}
        {/* 43.1 (RC-025): o GuidedTour está montado em todo o shell — o evento abre o tour
            in-place, na tela em que a usuária está. */}
        <DropdownMenuItem onClick={() => startRoleTour()}>Refazer tour</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem danger onClick={() => discardWork.run(() => { void handleLogout(); })}>
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

export interface TopbarProps {
  onBurger: () => void;
}

export function Topbar({ onBurger }: TopbarProps) {
  const title = usePageTitle();
  const allClinics = useLocation().pathname.replace(/\/+$/, '').toLowerCase() === '/clinicas';
  const [notifOpen, setNotifOpen] = useState(false);
  // 39.9 AC1: < lg não há campo de busca fixo — botão icon-only abre a busca
  // numa linha expandida do próprio header (sem Dialog — RC-005 fica na 39.8).
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 border-b border-sober-outline bg-[rgba(245,245,250,0.85)] backdrop-blur-lg">
      <div className="flex min-h-[72px] min-w-0 flex-wrap items-center justify-between gap-1 px-2 sm:gap-3 sm:px-4 lg:flex-nowrap lg:gap-4 lg:px-8">
      <button
        type="button"
        id="main-navigation-trigger"
        onClick={onBurger}
        aria-label="Abrir menu de navegação"
        className="grid h-11 w-11 shrink-0 place-items-center rounded-so-md text-sober-secondary hover:bg-sober-muted lg:hidden"
      >
        <Menu className="size-5" />
      </button>

      <h1 className="sr-only font-bold text-sober-text sm:not-sr-only sm:min-w-0 sm:flex-1 sm:truncate sm:text-so-20">{title}</h1>


      {/* 45.4: busca global REAL (o input era morto — sem handler algum) */}
      {!allClinics && <GlobalPatientSearch id="topbar-patient-search" className="hidden lg:block lg:w-48 xl:w-64" />}

      {!allClinics && <button
        type="button"
        onClick={() => setSearchOpen((o) => !o)}
        aria-label="Buscar"
        aria-expanded={searchOpen}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-so-md text-sober-secondary hover:bg-sober-muted lg:hidden"
      >
        <Search className="size-5" />
      </button>}

      <TenantSwitcher />

      {!allClinics && <button
        type="button"
        onClick={() => setNotifOpen(true)}
        aria-label="Notificações"
        className="relative grid h-11 w-11 shrink-0 place-items-center rounded-so-md text-sober-secondary hover:bg-sober-muted"
      >
        <Bell className="size-5" />
      </button>}

      <Link
        to="/config"
        aria-label="Configurações"
        className="hidden h-10 w-10 place-items-center rounded-so-md text-sober-secondary hover:bg-sober-muted sm:grid"
      >
        <Settings className="size-5" />
      </Link>

      <UserMenu />
      </div>

      {!allClinics && searchOpen && (
        <div className="flex items-center gap-2 border-t border-sober-outline px-4 py-2 lg:hidden">
          <GlobalPatientSearch
            id="topbar-patient-search-mobile"
            className="min-w-0 flex-1"
            autoFocus
            onNavigate={() => setSearchOpen(false)}
          />
          <button
            type="button"
            onClick={() => setSearchOpen(false)}
            aria-label="Fechar busca"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-so-md text-sober-secondary hover:bg-sober-muted"
          >
            <X className="size-5" />
          </button>
        </div>
      )}

      {!allClinics && <NotificationDrawer open={notifOpen} onOpenChange={setNotifOpen} />}
    </header>
  );
}
